import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { DateTime } from "@dashboard/components/Date/DateTime";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination/TablePaginationWithContext";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { AtticMovementKind, type AtticStockMovementFragment } from "@dashboard/graphql";
import { renderCollection } from "@dashboard/misc";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Input, Select, Skeleton, Text } from "@saleor/macaw-ui-next";
import { type FormEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { ledgerMessages, movementKindMessages, sectionMessages } from "../../messages";
import { type AtticLedgerUrlFilters, atticStockLookupUrl } from "../../urls";
import { signed } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";

const numberOfColumns = 7;

/** The kind select's first option: no kind filter. */
export const KIND_ALL = "";

export interface AtticLedgerPageProps {
  /** undefined while loading. */
  movements: AtticStockMovementFragment[] | undefined;
  loadFailed: boolean;
  filters: AtticLedgerUrlFilters;
  onFiltersChange: (changes: Partial<AtticLedgerUrlFilters>) => void;
}

/** What happened, as one line: the kind, and for a move or a reserve the detail. */
const What = ({ row }: { row: AtticStockMovementFragment }) => {
  const intl = useIntl();
  const kind = intl.formatMessage(movementKindMessages[row.kind]);

  if (row.kind === AtticMovementKind.MOVE && row.fromLocation && row.toLocation) {
    return (
      <Text size={3}>
        {kind}:{" "}
        {intl.formatMessage(ledgerMessages.fromTo, {
          from: row.fromLocation.code,
          to: row.toLocation.code,
        })}
      </Text>
    );
  }

  if (row.kind === AtticMovementKind.RESERVE) {
    return (
      <Text size={3}>
        {kind} (
        {intl.formatMessage(ledgerMessages.reserveDelta, { delta: signed(row.reservedDelta) })})
      </Text>
    );
  }

  const place = row.toLocation ?? row.fromLocation;

  return (
    <Text size={3}>
      {kind}
      {place ? ` @ ${place.code}` : ""}
    </Text>
  );
};

export const AtticLedgerPage = ({
  movements,
  loadFailed,
  filters,
  onFiltersChange,
}: AtticLedgerPageProps) => {
  const intl = useIntl();
  const [sku, setSku] = useState(filters.sku ?? "");
  const [location, setLocation] = useState(filters.location ?? "");

  useEffect(() => setSku(filters.sku ?? ""), [filters.sku]);
  useEffect(() => setLocation(filters.location ?? ""), [filters.location]);

  const kindOptions = [
    { label: intl.formatMessage(ledgerMessages.kindAll), value: KIND_ALL },
    ...Object.values(AtticMovementKind).map(kind => ({
      label: intl.formatMessage(movementKindMessages[kind]),
      value: kind,
    })),
  ];
  const kindValue = kindOptions.find(option => option.value === (filters.kind ?? KIND_ALL));
  const filtered = !!(filters.sku || filters.kind || filters.location);
  const applyText = () => {
    const nextSku = sku.trim().toUpperCase() || undefined;
    const nextLocation = location.trim().toUpperCase() || undefined;

    if (nextSku !== filters.sku || nextLocation !== filters.location) {
      onFiltersChange({ sku: nextSku, location: nextLocation });
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    applyText();
  };

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionMessages.ledger)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(ledgerMessages.subtitle)}
          </Text>
        }
      />
      <AtticInventoryNav current="ledger" />
      <DashboardCard data-test-id="attic-ledger">
        <DashboardCard.Content paddingX={0}>
          <form onSubmit={submit}>
            <Box
              display="flex"
              alignItems="flex-end"
              gap={4}
              flexWrap="wrap"
              paddingX={6}
              paddingTop={4}
              paddingBottom={2}
            >
              <Input
                size="small"
                label={intl.formatMessage(ledgerMessages.skuPlaceholder)}
                value={sku}
                onChange={event => setSku(event.target.value)}
                onBlur={applyText}
                data-test-id="attic-ledger-sku"
              />
              <Input
                size="small"
                label={intl.formatMessage(ledgerMessages.locationPlaceholder)}
                value={location}
                onChange={event => setLocation(event.target.value)}
                onBlur={applyText}
                data-test-id="attic-ledger-location"
              />
              <Select
                size="small"
                __width={200}
                label={intl.formatMessage(ledgerMessages.kind)}
                options={kindOptions}
                value={kindValue ?? kindOptions[0]}
                onChange={option =>
                  onFiltersChange({
                    kind: (option?.value || undefined) as AtticMovementKind | undefined,
                  })
                }
                data-test-id="attic-ledger-kind"
              />
              <button type="submit" hidden />
            </Box>
          </form>
          <ResponsiveTable footer={<TablePaginationWithContext component="div" />}>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(ledgerMessages.when)}</TableCell>
                <TableCell>{intl.formatMessage(ledgerMessages.skuPlaceholder)}</TableCell>
                <TableCell>{intl.formatMessage(ledgerMessages.what)}</TableCell>
                <TableCell align="right">{intl.formatMessage(ledgerMessages.change)}</TableCell>
                <TableCell>{intl.formatMessage(ledgerMessages.after)}</TableCell>
                <TableCell>{intl.formatMessage(ledgerMessages.who)}</TableCell>
                <TableCell style={{ width: "100%" }}>
                  {intl.formatMessage(ledgerMessages.reason)}
                </TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {loadFailed ? (
                <TableRowLink data-test-id="attic-ledger-failed">
                  <TableCell colSpan={numberOfColumns}>
                    <Text size={3} color="critical1">
                      {intl.formatMessage(sectionMessages.loadFailed)}
                    </Text>
                  </TableCell>
                </TableRowLink>
              ) : (
                renderCollection(
                  movements,
                  row => (
                    <TableRowLink key={row ? row.id : "skeleton"} data-test-id="attic-ledger-row">
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? (
                          <Text size={3} color="default2">
                            <DateTime date={row.createdAt} plain />
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? (
                          <Link to={atticStockLookupUrl({ sku: row.sku })}>
                            <Text size={3} style={{ fontFamily: MONO_FONT_FAMILY }}>
                              {row.sku}
                            </Text>
                          </Link>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? <What row={row} /> : <Skeleton />}
                      </TableCell>
                      <TableCell align="right" data-test-id="attic-ledger-change">
                        {row ? <Text size={3}>{signed(row.quantity)}</Text> : <Skeleton />}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }} data-test-id="attic-ledger-after">
                        {row ? (
                          <Text size={3} color="default2">
                            {row.onHandAfter} / {row.reservedAfter} / {row.sellableAfter}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }} data-test-id="attic-ledger-who">
                        {row ? (
                          <Text
                            size={3}
                            color={row.userEmail || row.appName ? "default1" : "default2"}
                          >
                            {row.userEmail ??
                              row.appName ??
                              intl.formatMessage(ledgerMessages.system)}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3} display="block" __whiteSpace="normal">
                            {row.reason}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="attic-ledger-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(
                            filtered ? ledgerMessages.noMatches : ledgerMessages.empty,
                          )}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticLedgerPage;
